import styled from "styled-components";

import { mq } from "../../styles/canonical/breakpoints";
import { getToneTokens } from "../../styles/shared";

// ---------- Filtros / lista ----------

export const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;

  .search {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 220px;
    padding: 0 12px;
    height: 38px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    color: var(--ink-2);
  }
  .search input {
    border: none;
    background: none;
    height: 100%;
    flex: 1;
    color: var(--ink);
    font-size: 13px;
    outline: none;
  }

  /* Celular/tablet: busca numa linha, os dois selects dividindo a segunda. */
  @media ${mq.tablet} {
    gap: 6px;

    .search {
      flex: 1 1 100%;
      min-width: 0;
      height: 36px;
    }
    > select {
      flex: 1 1 0;
      min-width: 0;
      height: 36px;
    }
  }
`;

export const Input = styled.input`
  height: 38px;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font-size: 13px;
  box-sizing: border-box;
  &::placeholder { color: var(--ink-2); }
  &:focus { outline: none; border-color: var(--accent); }
`;

export const Select = styled.select`
  height: 38px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font-size: 13px;
  cursor: pointer;
`;

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  ${({ $tone }) => {
    const t = getToneTokens(null, $tone || "neutral");
    return `background:${t.background}; color:${t.text}; border:1px solid ${t.border};`;
  }}

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
    padding: 2px 8px;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
`;

export const Th = styled.th`
  text-align: left;
  padding: 11px 14px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-2);
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
`;

export const Td = styled.td`
  padding: 11px 14px;
  color: var(--ink);
  border-bottom: 1px solid var(--border);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &.mono { font-variant-numeric: tabular-nums; color: var(--ink-2); }
  &.ellipsis { max-width: 0; }
`;

export const Tr = styled.tr`
  cursor: pointer;
  transition: background 0.12s ease;
  &:hover { background: var(--accent-soft); }
  &:last-child td { border-bottom: none; }
`;

export const IconBtn = styled.button`
  width: 30px;
  height: 30px;
  display: inline-grid;
  place-items: center;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-2);
  border-radius: 7px;
  cursor: pointer;
  &:hover { background: var(--accent-soft); color: var(--accent); }
`;

export const EmptyMsg = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--ink-2);
  font-size: 14px;
  border: 1px dashed var(--border);
  border-radius: 12px;
`;

export const ErrorMsg = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  background: var(--danger-bg);
  color: var(--danger-text);
  border: 1px solid var(--danger-border);
  font-size: 13px;
`;

export const Notice = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  background: var(--warning-bg);
  color: var(--warning-text);
  border: 1px solid var(--warning-border);
  font-size: 13px;
`;

// ---------- Editor ----------

export const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

export const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: var(--accent-soft); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: var(--accent);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover { filter: brightness(0.96); }
`;

export const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
`;

export const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  &:focus { outline: none; border-color: var(--accent); }
`;

export const ClienteHint = styled.div`
  font-size: 13px;
  color: var(--ink-2);
  span { color: var(--ink); font-weight: 600; }
`;

export const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  z-index: 30;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  max-height: 280px;
  overflow-y: auto;
`;

export const DropdownItem = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  text-align: left;
  padding: 9px 12px;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--ink);
  font-size: 13px;
  &:hover { background: var(--accent-soft); }
  small { color: var(--ink-2); font-size: 11px; }
`;

export const AmbienteCard = styled.div`
  border: 1px solid ${({ $error }) => ($error ? "var(--danger-border)" : "var(--border)")};
  border-radius: 12px;
  background: var(--surface);
  overflow: hidden;
`;

export const AmbienteHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  cursor: pointer;
  user-select: none;
`;

export const CheckMark = styled.span`
  width: 20px;
  height: 20px;
  flex: none;
  border-radius: 6px;
  display: grid;
  place-items: center;
  color: #fff;
  border: 1px solid ${({ $on }) => ($on ? "var(--accent)" : "var(--border)")};
  background: ${({ $on }) => ($on ? "var(--accent)" : "var(--surface)")};
`;

export const AmbienteNome = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: var(--ink);
`;

export const CountChip = styled.span`
  font-size: 11px;
  color: var(--accent);
  background: var(--accent-soft);
  padding: 2px 8px;
  border-radius: 999px;
`;

export const HeadHint = styled.span`
  margin-left: auto;
  font-size: 11px;
  color: var(--ink-2);
`;

export const AmbienteBody = styled.div`
  border-top: 1px solid var(--border);
  padding: 14px 16px 16px;
  background: var(--accent-soft);
`;

export const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  & + & { margin-top: 8px; }
`;

export const ItemNum = styled.span`
  width: 22px;
  flex: none;
  text-align: right;
  font-size: 11px;
  color: var(--ink-2);
  font-variant-numeric: tabular-nums;
`;

export const DelBtn = styled.button`
  width: 28px;
  height: 28px;
  flex: none;
  display: inline-grid;
  place-items: center;
  border: 1px solid transparent;
  background: transparent;
  color: var(--ink-2);
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: var(--danger-bg); color: var(--danger-text); }
`;

export const DelSpacer = styled.span`
  width: 28px;
  flex: none;
`;

export const AddItemBtn = styled.button`
  margin: 12px 0 0 30px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: 7px;
  background: transparent;
  color: var(--accent);
  border: 1px dashed var(--accent);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: var(--accent-soft); }
`;

export const InlineError = styled.div`
  font-size: 11px;
  color: var(--danger-text);
  margin: ${({ $indent }) => ($indent ? "10px 0 0 30px" : "2px 0 0")};
`;

// ---------- Documento (preview no modal) ----------

export const DocWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 0 8px;
`;
